import React from "react";
import Spinner from "@atlaskit/spinner";
import { PeopleCard, PeopleList, Pagination as PaginationUI } from "@caelor/ui";
import {
  Centered,
  PaginationWrapper,
  PeopleCardContainer,
  PeopleListContainer,
} from "./styled";
import { useTranslation } from "react-i18next";
import {
  NewPaginationTypesMapper,
  PaginationTypes,
} from "@Shared/Pagination-Types";
import { useStyleguideSetup } from "../../../hooks/use-styleguide-setup";
import { useStyleguideColors } from "../../../hooks/use-styleguide-colors";
import { DEFAULT_ACCENT_COLOR } from "../../people/defaults";
import usePeoplePagination from "../../../hooks/use-people-pagination";
import { useUpcomingBirthdayStore } from "../../../stores/use-upcoming-birthdays-store";
import { createDynamicProfileLink } from "../../../helpers/cosmos-profile-link";
import { useOpticsStore } from "../../../stores/use-optics-store";

export const getFormattedTitle = ({ daysLeft, formattedDateField }) => {
  if (daysLeft === 0) return <span>Today</span>;
  if (daysLeft === 1) return <span>Tomorrow</span>;

  return formattedDateField;
};

export const getFormattedFooter = ({ daysLeft }) => {
  if (daysLeft === 0) return <span style={{ fontSize: "12px" }}>Today</span>;

  return <span>{`${daysLeft} ${daysLeft === 1 ? "Day" : "Days"}`}</span>;
};

const Mapper = ({ data, children }) => data.map((user) => children(user));

const Variant = (props) => {
  const { variant, children, isLoading } = props;

  if (isLoading)
    return (
      <Centered>
        <Spinner size="xlarge" />
      </Centered>
    );

  if (variant === "card") {
    return (
      <PeopleCardContainer {...props}>
        {children(PeopleCard)}
      </PeopleCardContainer>
    );
  }
  return (
    <PeopleListContainer {...props}>{children(PeopleList)}</PeopleListContainer>
  );
};

const Pagination = ({ users = [], pagination, maxNumberOfItems, children }) => {
  const { t } = useTranslation();

  const { hasNextPage, itemsPaginated, maxPage, page, setPage } =
    usePeoplePagination({
      pagination,
      items: users,
      maxNumberOfItems: maxNumberOfItems || 4,
    });

  return (
    <PaginationWrapper>
      {children(itemsPaginated)}
      <PaginationUI
        type={NewPaginationTypesMapper[pagination]}
        labels={{
          next:
            pagination === PaginationTypes.INFINITE
              ? t("show-more")
              : t("next"),
          prev: t("prev"),
        }}
        hasNext={hasNextPage && page < maxPage}
        hasPrev={page > 1}
        onNext={() => hasNextPage && setPage(page + 1)}
        onPrev={() => page > 1 && setPage(page - 1)}
      />
    </PaginationWrapper>
  );
};

function UpcomingBirthday({ users, loading, cosmosIsNotSetup, noUsers, mode }) {
  const { data } = useUpcomingBirthdayStore((state) => ({
    data: state.data,
  }));

  const isCosmosInstalled = useOpticsStore((state) => state.isCosmosInstalled);

  const { styleguideSetup } = useStyleguideSetup();

  const styleguideColors = useStyleguideColors({
    styleguideSetup,
  });

  const {
    pagination,
    maxNumberOfItems,
    view,
    columns,
    borderRadius,
    accentColor,
    openInNewTab,
  } = data;

  return (
    <Pagination
      users={users}
      pagination={pagination}
      maxNumberOfItems={maxNumberOfItems}
    >
      {(paginatedData) => (
        <Variant variant={view} columns={columns} isLoading={loading}>
          {(PeopleComponent) => (
            <Mapper data={paginatedData}>
              {(user) => {
                return (
                  <div
                    key={user.accountId}
                    onClick={() =>
                      mode === "view"
                        ? window.open(
                            createDynamicProfileLink(
                              user.accountId,
                              isCosmosInstalled
                            ),
                            openInNewTab ?? true ? "_blank" : "_top"
                          )
                        : {}
                    }
                  >
                    <PeopleComponent
                      {...user}
                      key={user.accountId}
                      borderRadius={borderRadius}
                      emoji={user.daysLeft === 0 ? "🎉" : null}
                      accentColor={
                        accentColor ||
                        styleguideColors?.accent ||
                        DEFAULT_ACCENT_COLOR
                      }
                      subtitle={user.formattedDateField}
                      label={getFormattedFooter({ daysLeft: user.daysLeft })}
                    />
                  </div>
                );
              }}
            </Mapper>
          )}
        </Variant>
      )}
    </Pagination>
  );
}

export default UpcomingBirthday;
